import * as React from "react";
import {ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useMemo, useState} from "react";
import {Checkbox, Divider, FormControlLabel, Link, ThemeProvider, Tooltip} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import Box from "@mui/material/Box";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import Typography from "@mui/material/Typography";
import {Trans, useTranslation} from "react-i18next";
import {IWish} from "../../../store/wish/Helper";
import {obtainEntries} from "../../../store/ticket/ActionCreators";
import ReactGA from "react-ga4";
import {getWishRulebook} from "../../../store/wish/ActionCreators";
import {HelpOutline} from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import {validateEmail} from "../../../util/regex";
import {useTheme} from "@mui/material/styles";
import {toastSuccess} from "../../shared/toast/Toast";

interface IProps {
    open: boolean
    handleDialogClose: () => void
    resetTicketCount: () => void
    wish: IWish
    count: number
    setHasParticipated: Dispatch<SetStateAction<boolean>>
}

const ObtainEntriesDialog: FC<IProps> = (props) => {
    const initialGiftState = useMemo(() => ({
        isGift: false,
        wish_id: props.wish.id,
        ticket_count: props.count,
        recipient_email: '',
        recipient_name: '',
        personal_message: ''
    }), [props.count, props.wish.id])
    const {i18n, t} = useTranslation()
    const dispatch = useAppDispatch()
    const obtainEntriesLoading = useAppSelector((state: RootState) => state.ticketReducer.loading)
    const availablePoints = useAppSelector((state: RootState) => state.authReducer.loggedInUser.available_points) ?? 0
    const rulebook = useAppSelector((state: RootState) => state.wishReducer.selectedRulebook)
    const [giftForm, setGiftForm] = useState(initialGiftState)
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false)
    const [isNameValid, setIsNameValid] = useState<boolean>(false)
    const theme = useTheme()

    useEffect(() => {
        if (props.open) {
            setGiftForm(initialGiftState)
            dispatch(getWishRulebook(props.wish.id))
        }
    }, [dispatch, props.wish.id, props.open, initialGiftState])

    const canObtainEntries = availablePoints >= props.count * props.wish.cost

    const updateHasParticipated = () => {
        if (props.wish.cost === 0) {
            props.setHasParticipated(true)
        }
        props.resetTicketCount()
        props.handleDialogClose()
        !giftForm.isGift ? toastSuccess(t('success_obtain_entries', {
            count: props.count
        })) : toastSuccess(t('success_gift_obtain_entries', {
            count: props.count
        }))

    }

    const handleObtainEntries = () => {
        if (!canObtainEntries) {
            props.handleDialogClose()
            return
        }
        ReactGA.event({
            category: 'Wish',
            action: 'make_a_wish_click_confirm',
            label: `Make a wish - Confirm - ${props.wish.id}`,
        })
        const {isGift, recipient_name, recipient_email, personal_message, ...requestPayload} = giftForm

        dispatch(obtainEntries({
            body: {
                ...requestPayload,
                ...(isGift && {
                    recipient_email,
                    recipient_name,
                    personal_message
                })
            },
            updateHasParticipated
        }))
    }

    const handleOnInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.name === 'recipient_email' && validateEmail(e.target.value)) {
            setIsEmailValid(true)
        }
        if (e.target.name === 'recipient_name' && e.target.value.length > 0) {
            setIsNameValid(true)
        }
        setGiftForm((oldForm) => (
            {
                ...oldForm,
                [e.target.name]: e.target.value
            }
        ))
    }

    const handleChangeIsGift = () => {
        setGiftForm((oldForm) => ({
            ...oldForm,
            isGift: !oldForm.isGift,
            recipient_email: '',
            recipient_name: '',
            personal_message: ''
        }))
        setIsEmailValid(true)
        setIsNameValid(true)
    }

    const handleOnBlurEmailField = () => {
        setIsEmailValid(validateEmail(giftForm.recipient_email ?? ''))
    }

    const handleOnBlurNameField = () => {
        setIsNameValid(giftForm.recipient_name.length > 0)
    }

    const isFormValid = giftForm.isGift &&
        (!validateEmail(giftForm.recipient_email) || !giftForm.recipient_name.length)

    const dialogActions = (
        <LoadingButton
            variant="contained"
            loading={obtainEntriesLoading}
            onClick={handleObtainEntries}
            disabled={isFormValid}
        >
            {canObtainEntries ? t('confirm') : t('close')}
        </LoadingButton>
    )

    const dialogContent = (
        <Box my={2}>
            <Typography variant='subtitle1' color='primary'>
                {canObtainEntries ? t('obtain_entries_confirmation', {
                    count: props.count,
                    wishName: props.wish[`name_${i18n.language}`]
                }) : t('not_enough_points_message', {
                    count: props.count,
                    wishName: props.wish[`name_${i18n.language}`],
                    currentPoints: availablePoints,
                    missingPoints: props.count * props.wish.cost - availablePoints
                })}
            </Typography>
            {canObtainEntries &&
                <Typography variant='subtitle1' color='primary' fontWeight="600" sx={{textDecoration: "underline"}}
                            mt={2}>
                    {props.wish.cost === 0 ? t('giveaway_note') : t('cost_genie_points', {
                        count: props.count * props.wish.cost
                    })}
                </Typography>}

            {canObtainEntries && props.wish.cost !== 0 && <>
                <FormControlLabel sx={{mt: 2, mb: 1}} control={<Checkbox sx={{color: theme.palette.primary.main}}
                                                                         value={giftForm.isGift}
                                                                         checked={giftForm.isGift}
                                                                         color="primary"
                                                                         onChange={handleChangeIsGift}
                                                                         size={'small'}/>}
                                  label={
                                      <Typography variant="body1" color="primary">
                                          {t('send_as_gift')}
                                          <Tooltip enterTouchDelay={0} title={t('send_entry_as_gift_tooltip')} arrow
                                                   placement="top">
                                              <HelpOutline sx={{width: '15px', height: '15px', p: 0, ml: 0.5}}
                                                           onClick={(e) => e.preventDefault()}/>
                                          </Tooltip>
                                      </Typography>
                                  }/>
                {giftForm.isGift && <>
                    <TextField
                        required
                        color="primary"
                        type="email"
                        fullWidth
                        name="recipient_email"
                        value={giftForm.recipient_email}
                        onChange={handleOnInputChange}
                        label={t('recipient_email')}
                        error={!isEmailValid}
                        helperText={!isEmailValid && t('email_field_not_valid')}
                        onBlur={handleOnBlurEmailField}
                    />

                    <TextField
                        color="primary"
                        type="text"
                        required
                        fullWidth
                        name="recipient_name"
                        value={giftForm.recipient_name}
                        onChange={handleOnInputChange}
                        label={t('recipient_name')}
                        error={!isNameValid}
                        helperText={!isNameValid && t('field_required')}
                        onBlur={handleOnBlurNameField}
                        sx={{my: 2}}
                    />

                    <TextField fullWidth
                               color="primary"
                               value={giftForm.personal_message}
                               name="personal_message"
                               onChange={handleOnInputChange}
                               multiline
                               rows={3}
                               label={t('personalized_message')}/>
                </>}
            </>}
            {canObtainEntries && <>
                <Divider sx={{backgroundColor: theme.palette.primary.main, mt: 2}}/>
                <Typography variant='body2' color="primary" mt={1}>
                    <Trans i18nKey="obtain_entries_notice"
                           components={[<Link sx={{cursor: 'pointer'}} color="primary" href={rulebook.doc_url}
                                              target="_blank"
                                              rel="noopener noreferrer"/>]}/>
                </Typography>
            </>}
        </Box>

    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog open={props.open}
                          title={t('make_a_wish')}
                          maxWidth='sm'
                          actions={dialogActions}
                          handleDialogClose={props.handleDialogClose}
                          content={dialogContent}/>
        </ThemeProvider>
    )
}

export default ObtainEntriesDialog
